import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardMedia, Link } from '@americanexpress/dls-react';
import { IconPlus } from '@americanexpress/dls-icons';
import { useSelector } from 'react-redux';
import {
  useCommonContent,
  useOneCmsLocalePartners,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import {
  enrollmentStatuses,
  usePriorityPassEnrollmentDetails,
  usePriorityPassEnrollmentUrl,
} from '@/hooks/priorityPassHooks/priorityPassHelperHooks';
import { cn } from '@/helpers/utils';
import { getIsLoggedIn } from '@/selectors/authSelectors';
import {
  TclMigrationFeatures,
  useTCLFeatureFlags,
} from '@/hooks/featureFlagHooks';
import { useAnalyticsContext } from '@/context/analyticsContext';
import LFCarousel from '../common/carousel/LFCarousel';
import styles from './NewLandingPartnerLogosSection.module.scss';
import LoungeTypeBadge from '../common/LoungeTypeBadge';
import LFLink from '../common/LFLink';

const GLOBAL_LOUNGE_COLLECTION_PARTNER_NAME =
  'The American Express Global Lounge Collection®';

const NewLandingPartnerLogosSection = () => {
  const { customTrack } = useAnalyticsContext();
  const partners = useOneCmsLocalePartners();
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const { partners: commonContentPartners } = useCommonContent();
  const { enabled: isPriorityPassEnabled, status: enrollmentStatus } =
    usePriorityPassEnrollmentDetails();
  const enrollmentUrl = usePriorityPassEnrollmentUrl();
  const isEligibleCNMarket = useTCLFeatureFlags(
    TclMigrationFeatures.CENTURION_NETWORK_PAGE
  );
  const isLoggedIn = useSelector(getIsLoggedIn);

  const [isMobile, setIsMobile] = useState(false);

  const isPriorityPassNotEnrolled =
    isPriorityPassEnabled &&
    enrollmentStatus === enrollmentStatuses.NOT_ENROLLED;
  const shouldShowPriorityPassEnrollmentCTA =
    isLoggedIn && isPriorityPassNotEnrolled;

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 567px)');
    setIsMobile(mobileQuery.matches);

    const handleResize = (e) => setIsMobile(e.matches);
    mobileQuery.addEventListener('change', handleResize);
    return () => mobileQuery.removeEventListener('change', handleResize);
  }, []);

  let displayedPartners = partners.filter((partner) => partner.newLogo);
  const hasGlobalLoungeCollectionPartner = displayedPartners.some(
    (item) => item.partner_name === GLOBAL_LOUNGE_COLLECTION_PARTNER_NAME
  );

  if (isMobile && !hasGlobalLoungeCollectionPartner) {
    displayedPartners.unshift({
      newLogo: 'the-centurion-network-lounge-mobile.png',
      partner_name: GLOBAL_LOUNGE_COLLECTION_PARTNER_NAME,
    });
  }

  if (!isMobile && hasGlobalLoungeCollectionPartner) {
    displayedPartners = displayedPartners.filter(
      (partner) =>
        partner.partner_name !== GLOBAL_LOUNGE_COLLECTION_PARTNER_NAME
    );
  }

  return (
    <>
      {!isMobile && (
        <h2 className="tw-heading-sm-bold tw-mb-6 tw-mt-10 tw-text-gray-700">
          {commonContentPartners.sectionHeader || 'All Partners'}
        </h2>
      )}
      <LFCarousel
        id="partners-carousel"
        data-testid="partners-carousel"
        innerContainerClassName="tw-rounded-2xl"
      >
        {displayedPartners?.map(
          ({ newLogo: partnerLogo, partner_name: partnerName }) => {
            return (
              <div key={partnerLogo}>
                <Card
                  key={partnerName}
                  className={`tw-w-full tw-rounded-2xl tw-border tw-border-gray-300 ${partnerName !== 'The American Express Global Lounge Collection®' ? 'first-child_tw-flex first-child_tw-items-center first-child_tw-justify-center first-child_tw-shadow-none' : ''} ${partnerName === 'The American Express Global Lounge Collection®' ? 'tw-border-none tw-px-0 first-child_tw-bg-blue-deep-700' : ''}`}
                  innerContainerClassName="tw-p-0 tw-rounded-2xl"
                >
                  {isMobile &&
                  partnerName ===
                    'The American Express Global Lounge Collection®' ? (
                    <div className="tw-bg-blue-deep-700">
                      <CardMedia className="tw-flex tw-px-0">
                        <img
                          src={`${baseOneCmsImgUrl}${partnerLogo}`}
                          alt={partnerName}
                          style={{ height: '190px' }}
                        />
                      </CardMedia>
                      <CardContent alignment="center" className="tw-m-6 tw-p-0">
                        <h2
                          className={cn(
                            'tw-heading-md-medium tw-mb-1 tw-p-0 tw-text-left tw-text-gray-100'
                          )}
                        >
                          {commonContentPartners.cnHeader}
                        </h2>
                        <p className={cn('tw-text-left tw-text-gray-100')}>
                          {commonContentPartners.cnDescription}
                        </p>
                        {isEligibleCNMarket && (
                          <div className="tw-w-full">
                            <LFLink
                              reactlytics={{ disable: true }}
                              className="btn primary btn-primary tw-mt-4 tw-w-full tw-max-w-none"
                              to="centurion-network"
                              onClickHandler={(event) =>
                                customTrack({
                                  event,
                                  element:
                                    'The Centurion Lounge Banner>>View Details',
                                  detail: 'Click View Details ',
                                })
                              }
                            >
                              {commonContentPartners.cnCTALabel}
                            </LFLink>
                          </div>
                        )}
                      </CardContent>
                    </div>
                  ) : (
                    <div>
                      {partnerName.includes('Priority Pass') && (
                        <>
                          <LoungeTypeBadge
                            badgeType="priorityPass"
                            className={styles.loungeBadgeType}
                          />
                          {shouldShowPriorityPassEnrollmentCTA && (
                            <Link
                              className={styles.loungeBadgeIconLink}
                              href={enrollmentUrl}
                              isInline
                              aria-label="Enroll in Priority Pass"
                              onClick={(event) =>
                                customTrack({
                                  event,
                                  element: 'Our Partners>>Priority Pass',
                                  detail: 'Click Our Partners',
                                })
                              }
                              reactlytics={{ disable: true }}
                            >
                              <IconPlus aria-hidden="true" size="xs" />
                            </Link>
                          )}
                        </>
                      )}
                      <CardMedia className={styles.partnerLogoImage}>
                        <img
                          src={`${baseOneCmsImgUrl}${`${partnerLogo}-default.png`}`}
                          alt={partnerName}
                          className={styles.partnerLogoImageDefault}
                        />

                        <img
                          src={`${baseOneCmsImgUrl}${`${partnerLogo}-hover.png`}`}
                          alt={partnerName}
                          className={styles.partnerLogoImageHover}
                        />
                      </CardMedia>
                    </div>
                  )}
                </Card>
              </div>
            );
          }
        )}
      </LFCarousel>
    </>
  );
};

export default NewLandingPartnerLogosSection;
